/**
 * F3 · El contrato del menú. **Una sola definición de cada forma.**
 *
 * Este archivo es la costura por donde se integran la API, el editor del panel y
 * el menú público. Se escribió **antes** que cualquiera de los tres, y a
 * propósito: dos lados que se ponen de acuerdo después de escribirse acaban
 * discrepando, y la discrepancia no da error — da una pantalla en blanco.
 *
 * Ya pasó en pequeño el 2026-09-09: la consola redefinía a mano lo que devolvía
 * la API y `vence` volvía como `Date` de un lado y como cadena del otro, con el
 * tipo declarado mintiendo. Multiplicado por tres implementaciones a la vez, eso
 * es una tarde perdida.
 *
 * > [!danger] Este archivo está CONGELADO durante la ola 2
 * > Quien crea que una forma de aquí está mal, **para y lo dice**. No la cambia:
 * > cambiarla mientras otros dos construyen contra ella es cómo se rompe lo que
 * > este archivo existe para evitar.
 */
import { z } from 'zod';
/* ── Piezas comunes ───────────────────────────────────────────────────────── */
/** Los identificadores viajan como cadena; validarlos aquí evita un 500 por un
 *  `where id = 'hola'`, que no devuelve cero filas: revienta. */
export const esquemaId = z.string().uuid();
/**
 * Un platillo se publica o no. **No hay un tercer estado**: «agotado» es
 * disponibilidad y es otra columna, de `T-34`. Mezclarlos haría que quitar algo
 * de la carta por un día y quitarlo para siempre fueran lo mismo.
 */
export const esquemaEstadoDePlatillo = z.enum(['draft', 'published']);
/**
 * Precio en **centavos enteros**. Nunca decimales: `Estandares.md` lo exige y el
 * motor lo respalda con un `check (price_cents >= 0)`.
 *
 * El tope no es capricho: 2 000 000 centavos son veinte mil pesos por platillo.
 * Un precio más alto que eso es un dedo, no una carta.
 */
export const esquemaCentavos = z.number().int().min(0).max(2_000_000);
/** Lo que se escribe en una pantalla de móvil, no en una hoja de cálculo. */
const nombre = z.string().trim().min(1).max(80);
/**
 * `.min(1)` y no sólo `.max(280)`: sin él, `''` y `'   '` PASAN el contrato —el
 * `trim()` los deja en cadena vacía— y llegan al motor, que los rechaza con
 * `check (description is null or length(trim(description)) > 0)`. Resultado: un
 * **500 donde debía haber un 400**, que es letra por letra lo que la cabecera de
 * la migración 035 dice que se quiso evitar. Medido por HTTP contra la API viva
 * antes de tocar nada: `{descripcion:''}` → 500, `{descripcion:'   '}` → 500.
 *
 * Borrar la descripción se pide con `null`, que sí es un caso legítimo y tiene su
 * propio camino. La cadena vacía no es «bórrala»: es un formulario mandado sin
 * querer, y el dueño merece que se lo digan.
 */
const descripcion = z.string().trim().min(1).max(280);
/**
 * `T-37` · El nombre de un tamaño, que **no es el nombre de un platillo**.
 *
 * 24 caracteres y no 80, el mismo tope que el `check` de la migración 034. No es
 * prudencia: esto se pinta como una **ficha** —«C · M · G»— en la fila de la
 * tabla del armador, al lado de otras dos. Un tamaño llamado «el grande de toda
 * la vida para compartir» no cabe en ninguna pantalla, y el sitio donde eso debe
 * doler es al escribirlo, no al pintarlo.
 *
 * Texto libre y **no** un conjunto cerrado: «12 oz», «Jarra», «Individual» y
 * «Familiar» son nombres de tamaño tan reales como «Chico». Decidir por el dueño
 * cómo se llama lo que vende sería inventar una regla que nadie pidió.
 */
const nombreDeTamano = z.string().trim().min(1).max(24);
/**
 * `T-38` · Las calorías de un platillo. **Un entero, y opcional.**
 *
 * Lo decidió Isaac con esas palabras: «es por platillo y es un campo opcional».
 * Por tamaño no — la plantilla del menú público llegó a insinuarlo («350 ml ·
 * 180 kcal» por ficha) y se descarta donde se decide, que es aquí.
 *
 * ── Nulo NO es cero ────────────────────────────────────────────────────────
 * «No lo hemos medido» y «no tiene calorías» son cosas distintas, y en una
 * cafetería sólo una de las dos es creíble: el agua tiene 0 y todo lo demás
 * tiene algo. Por eso «no se sabe» es `null` y nunca `0`, tanto aquí como en la
 * columna, que es NULLABLE y lo dice en su comentario.
 *
 * ── El tope ────────────────────────────────────────────────────────────────
 * 10 000 kcal. El platillo más pesado de un mostrador anda por debajo de 3 000,
 * así que deja tres veces de margen y aun así **para el dígito de más**: 1 700 →
 * 17 000 se rechaza. Es el mismo número que el `check` de la migración 037, y lo
 * es a propósito: éste da un 400 legible, aquél protege también a quien entra
 * por el editor SQL. La misma pareja que `esquemaCentavos` y `price_cents >= 0`.
 */
export const esquemaCalorias = z.number().int().min(0).max(10_000);
/**
 * `T-38` · El nombre de un **grupo** de tamaños, que no es el de una ficha.
 *
 * 40 y no 24: esto no se pinta como una ficha en la fila de la tabla del
 * armador, se lee en un desplegable de una línea —«Café caliente», «Bebidas
 * frías de la casa»—, y ahí caben más caracteres sin romper nada.
 */
const nombreDeGrupo = z.string().trim().min(1).max(40);
/* ── Lo que la API DEVUELVE ───────────────────────────────────────────────── */
/**
 * `T-37` · Un tamaño de un platillo, con **su** precio.
 *
 * Viaja en los dos lados —el editor y el menú público— porque un tamaño es un
 * precio, y un precio que el comensal no ve es un menú a medias.
 */
export const esquemaTamano = z.object({
    id: esquemaId,
    nombre: nombreDeTamano,
    precioCentavos: esquemaCentavos,
    /** C, M, G no es alfabético. Lo decide el dueño, igual que el de los apartados. */
    orden: z.number().int(),
});
export const esquemaPlatillo = z.object({
    id: esquemaId,
    nombre,
    descripcion: z.string().nullable(),
    precioCentavos: esquemaCentavos,
    estado: esquemaEstadoDePlatillo,
    orden: z.number().int(),
    /**
     * `T-35` · La foto, ya como **URL completa** y no como ruta de objeto.
     *
     * Nula es el caso normal: casi ninguna carta tendrá foto en todos sus
     * platillos, y `ADR-013` dice que el menú tiene que verse bien sin ninguna.
     *
     * Es la API quien compone la URL, y no el navegador a partir de la ruta, por
     * dos razones que van juntas: el navegador **no tiene por qué saber dónde
     * vive Storage** —hoy Supabase, mañana quizá no—, y si lo supiera habría dos
     * sitios donde equivocarse con el origen. Es la misma decisión que tomó
     * `T-33` al completar la URL de subida dentro del módulo aislado.
     */
    imagenUrl: z.string().url().nullable(),
    /**
     * `072` · La estrellita del dueño.
     *
     * Sale en «Lo que recomendamos» de la pantalla de búsqueda y con una estrella
     * delante del nombre en la carta. **No es «lo más pedido»**: FialRest no toma
     * comandas, así que ese número no existe — lo marca el dueño a mano.
     */
    destacado: z.boolean(),
    /**
     * `T-37` · Los tamaños, **con su precio cada uno**. Lista vacía = precio
     * único, que es el caso normal y el que la pantalla pinta como «Único».
     *
     * ── Por qué va DENTRO de `esquemaPlatillo` y no en un esquema extendido ───
     * `archivadoEn` se añadió extendiendo (`esquemaPlatilloDelEditor`) porque es
     * un dato que **nunca** debe llegar al comensal: que no quepa en el tipo
     * público es la primera de sus tres barreras. Aquí es al revés — los tamaños
     * **siempre** tienen que llegar al comensal, porque son los precios. Meterlos
     * en un esquema aparte permitiría servir una carta sin ellos y que compilara,
     * que es exactamente el fallo que se quiere hacer imposible.
     *
     * Es un campo nuevo en una forma existente, o sea **añadir**, que es lo único
     * que el congelado de este archivo permite. Y el compilador señala a todos los
     * que producen un `Platillo`, que es el efecto buscado: quien devuelve un
     * platillo tiene que decidir qué tamaños tiene, no olvidarse.
     *
     * ── Lista vacía y no nulo ─────────────────────────────────────────────────
     * Un nulo añadiría un tercer estado —«no se sabe»— que ninguna consulta
     * produce y que la pantalla tendría que distinguir de «no tiene». La regla de
     * la pantalla es una sola: `tamanos.length === 0` es «Único».
     *
     * ── Y qué es `precioCentavos` cuando esto no está vacío ───────────────────
     * El **más barato de estos**, y lo garantiza el motor: la migración 034 lo
     * mantiene como espejo de `min(menu_item_sizes.price_cents)` y rechaza
     * cualquier otro valor. O sea que el precio de la lista es un «desde» honrado
     * —nunca mayor que algo que de verdad se puede pedir— y no una segunda verdad
     * que pueda discrepar de las fichas.
     */
    tamanos: z.array(esquemaTamano),
});
export const esquemaCategoria = z.object({
    id: esquemaId,
    nombre,
    /**
     * `T-37` · Lo que se enseña **bajo el título de la sección**, en la mesa.
     *
     * Nula es el caso normal: no toda cafetería describe sus apartados, igual que
     * no toda describe sus platillos. Y nula —nunca cadena vacía— porque «no la
     * escribió» y «la borró» tienen que ser el mismo estado y verse igual; el
     * motor lo respalda con `check (description is null or length(trim(…)) > 0)`,
     * el mismo que `menu_items.description` lleva desde la 025.
     *
     * **Sí viaja al comensal**, y por eso vive en la forma pública y no sólo en la
     * del editor. `visible` es al revés: ver más abajo.
     */
    descripcion: z.string().nullable(),
    orden: z.number().int(),
    platillos: z.array(esquemaPlatillo),
});
export const esquemaCarta = z.object({
    id: esquemaId,
    nombre,
    /** Nulo = vale para toda la cafetería. Con valor, sólo para esa sucursal. */
    sucursalId: esquemaId.nullable(),
    categorias: z.array(esquemaCategoria),
});
export const esquemaCartas = z.object({ cartas: z.array(esquemaCarta) });
/* ── Lo que el editor MANDA ───────────────────────────────────────────────── */
/**
 * `T-34` pide **guardado por platillo**, no un «Guardar» global. Quien edita el
 * menú de su cafetería lo hace de pie y entre clientes: un fallo de red tiene
 * que costar un platillo, no la carta entera.
 *
 * Por eso cada esquema de escritura describe **una** cosa, y por eso son
 * `PATCH` parciales y no `PUT` completos.
 */
export const esquemaCrearCarta = z.object({
    nombre,
    sucursalId: esquemaId.nullable(),
}).strict();
export const esquemaCrearCategoria = z.object({
    cartaId: esquemaId,
    nombre,
}).strict();
export const esquemaCrearPlatillo = z.object({
    categoriaId: esquemaId,
    nombre,
    descripcion: descripcion.optional(),
    precioCentavos: esquemaCentavos,
    /**
     * `T-38` · Opcional, y **ausente** en vez de nula: un platillo que nace sin
     * calorías es el caso normal, y `null` en el alta significaría exactamente lo
     * mismo que no mandar nada. La diferencia entre nulo y ausente sólo existe
     * donde hay algo anterior que borrar, o sea en el `PATCH`.
     */
    calorias: esquemaCalorias.optional(),
}).strict();
/**
 * Cambio parcial de un platillo. Todo opcional salvo que **algo** tiene que
 * cambiar: un cuerpo vacío gastaría una transacción y un renglón de
 * `menu_change_log` para no cambiar nada.
 *
 * `descripcion: null` es «bórrala»; ausente es «no la toques». La diferencia se
 * pierde si se usa `coalesce` en el SQL — en el motor los dos son el mismo nulo,
 * así que se decide fuera.
 */
export const esquemaEditarPlatillo = z.object({
    nombre: nombre.optional(),
    descripcion: descripcion.nullable().optional(),
    precioCentavos: esquemaCentavos.optional(),
    estado: esquemaEstadoDePlatillo.optional(),
    /**
     * `T-38` · **`null` es «bórralas»; ausente es «no las toques».** Idéntico a
     * `descripcion`, y por la misma razón: en el motor los dos son el mismo nulo,
     * así que la diferencia se decide fuera o se pierde.
     *
     * Y es la diferencia que importa aquí más que en ningún otro campo: borrar el
     * dato tiene que poder decirse, porque una cafetería que midió mal prefiere no
     * decir nada antes que decir un número falso.
     */
    calorias: esquemaCalorias.nullable().optional(),
}).strict().refine((c) => Object.keys(c).length > 0, { message: 'Hay que cambiar algo: nombre, descripción, precio, calorías o estado.' });
/**
 * Reordenar. Llega **la lista entera de ids en su nuevo orden**, no «sube uno».
 *
 * Con «sube uno» dos pestañas abiertas producen órdenes distintos y gana la
 * última sin que nadie lo note. Con la lista completa, quien manda una lista que
 * no coincide con lo que hay se entera.
 */
export const esquemaReordenar = z.object({
    ids: z.array(esquemaId).min(1).max(200),
}).strict();
/* ── Lo que ve el EDITOR, que no es lo que ve el comensal ─────────────────── */
/**
 * `T-38` · El nombre de un extra, que **no es el de un tamaño ni el de un
 * platillo**.
 *
 * 40 caracteres, el mismo tope que el `check` de la migración 038. Ni 24 —el del
 * tamaño— ni 80 —el del platillo—, y los dos límites tienen su motivo: un tamaño
 * se pinta como una ficha diminuta dentro de una celda de la tabla del armador, y
 * un extra se pinta como un **chip en una nube con `flex-wrap`**, que baja de
 * línea sola. «Leche de almendras» son 18 caracteres y es un nombre corriente;
 * uno que ocupa la fila entera deja de ser un chip.
 */
const nombreDeExtra = z.string().trim().min(1).max(40);
/**
 * `T-46` · Un extra **sin decir de quién es**: nombre, precio e interruptor.
 *
 * Existe partido en dos por el orden de este archivo, no por diseño: el producto
 * del editor se declara cuatrocientas líneas antes que `esquemaExtra`, y lleva
 * sus extras propios dentro. Sube la base —lo que un extra es— y `esquemaExtra`
 * la extiende con su grupo allá abajo, donde vive el resto del catálogo.
 *
 * Lo que se gana con esto y no con una copia: el nombre, el precio y el
 * interruptor se declaran **una vez**, y el día que un extra gane un campo lo
 * gana en los dos sitios o en ninguno.
 */
export const esquemaExtraPropio = z.object({
    id: esquemaId,
    nombre: nombreDeExtra,
    /** Centavos enteros. **Cero es legítimo** y significa «sin cargo». */
    precioCentavos: esquemaCentavos,
    /**
     * `false` = «este mes no hay crema batida».
     *
     * En positivo y no `agotado`, con la misma polaridad que la columna
     * `is_available` y que el predicado — invertirla en una capa sería un sitio
     * donde equivocarse en cada lectura, que es lo que `esquemaCategoriaDelEditor`
     * ya razonó para `visible`. La negación de la vista la hace la vista.
     */
    disponible: z.boolean(),
});
/**
 * `T-34` · El dueño ve cosas que el comensal no: platillos en borrador y, sobre
 * todo, **la papelera**.
 *
 * Se define **extendiendo** y no tocando `esquemaPlatillo`, que está congelado
 * desde la primera ola y lo consume el menú público. No es formalismo: si el
 * platillo archivado cupiera en el contrato público, el día que alguien se
 * despistara en una consulta un platillo retirado aparecería en la carta de una
 * mesa. Que no quepa en el tipo es la primera de las tres barreras — las otras
 * dos son la policy y el `where`.
 */
/**
 * `T-48` · El cuerpo de `PUT /menu/platillos/:id/sucursales`.
 *
 * **Reemplaza la lista entera**, no añade ni quita de una en una, y por eso la
 * ruta es `PUT`: el panel enseña un grupo de casillas y guarda lo que quedó
 * marcado. Con `POST`/`DELETE` por sucursal, dos pestañas abiertas acabarían
 * mezclando dos decisiones distintas en una lista que nadie eligió.
 *
 * **Lista vacía es válida y significa «en todas»** — es como se deshace una
 * exclusividad, y tiene que ser expresable.
 */
export const esquemaDondeSeVende = z.object({
    sucursales: z.array(esquemaId),
});
/**
 * `072` · La estrellita del dueño — `PUT /menu/platillos/:id/destacado`.
 *
 * Un `PUT` con el valor entero y no un `POST`/`DELETE`: la pantalla enseña un
 * interruptor y guarda en qué posición quedó. Es la misma forma que
 * `esquemaDondeSeVende` y por lo mismo — con dos verbos, dos pestañas abiertas
 * acabarían dejando el platillo en un estado que nadie eligió.
 */
export const esquemaDestacado = z.object({
    destacado: z.boolean(),
});
/** `T-48` · Una sucursal, como la necesita el selector del editor. */
export const esquemaSucursalDelEditor = z.object({
    id: esquemaId,
    nombre: z.string().min(1),
});
export const esquemaPlatilloDelEditor = esquemaPlatillo.extend({
    /** ISO 8601, o nulo si está en la carta. Nunca llega con valor al público. */
    archivadoEn: z.string().datetime().nullable(),
    /**
     * `T-38` · Las calorías, **y aquí y no en `esquemaPlatillo`**.
     *
     * Es la decisión de Isaac —«panel primero, mesa después»— escrita como tipo.
     * El criterio de este archivo ya estaba fijado por `archivadoEn` y por
     * `visible`: lo que no debe llegar al comensal vive en la forma del editor,
     * «que no quepa en el tipo público es la primera de sus tres barreras». Las
     * otras dos, aquí, son que `anon` no tiene otorgada la columna `calories` y
     * que `menu-publico.ts` no la nombra.
     *
     * Es justo al revés que `tamanos`, que vive en la forma pública porque
     * **siempre** tiene que llegar a la mesa. La asimetría es deliberada y va en
     * la dirección barata: **mover un campo a la forma pública el día que se
     * decida enseñarlo es un `extend` menos; sacarlo de ella después de haberlo
     * publicado ya no se puede sin coordinar un despliegue.**
     *
     * **Nula = no se han medido**, y no es lo mismo que cero. Ver `esquemaCalorias`.
     */
    calorias: esquemaCalorias.nullable(),
    /**
     * `T-38` · **Qué extras del catálogo acepta este platillo**, por identificador.
     *
     * ── Por qué aquí y NO en `esquemaPlatillo`, al revés que `tamanos` ─────────
     * `tamanos` se metió en la forma pública porque un tamaño **es un precio** y un
     * precio que el comensal no ve es un menú a medias: dejarlo fuera habría
     * permitido servir una carta sin precios y que compilara.
     *
     * Aquí es al contrario, y es una decisión de Isaac, no una consecuencia:
     * **«panel primero, mesa después»**. Los extras no llegan al comensal en esta
     * ola —la migración 038 no otorga una sola columna a `anon` y `menu-publico.ts`
     * no se toca—, así que ponerlos en la forma pública habría obligado a
     * producirlos allí y habría convertido una decisión de alcance en un tipo que
     * empuja hacia el lado contrario. Que no quepa en el tipo público es la misma
     * primera barrera que protege a `archivadoEn`.
     *
     * ── Identificadores y no fichas ───────────────────────────────────────────
     * La lista son `id`s del catálogo, que se pide aparte (`GET /menu/extras`). Si
     * viajaran las fichas enteras, **el precio de «Shot extra» estaría repetido en
     * cada platillo que lo lleva**, y una carta de sesenta platillos podría llegar
     * con dos precios distintos para el mismo extra si algo se desincronizara — que
     * es exactamente la segunda verdad sobre un precio que el catálogo existe para
     * no tener. Cuesta una petición más al abrir el editor, y a cambio hay un solo
     * sitio donde vive cada precio.
     *
     * ── Lista vacía y no nulo ─────────────────────────────────────────────────
     * Por lo mismo que `tamanos`: un nulo añadiría un tercer estado —«no se sabe»—
     * que ninguna consulta produce. `extras.length === 0` es «ningún chip relleno».
     */
    extras: z.array(esquemaId),
    /**
     * `T-46` · Los extras **propios de este producto**, enteros y no por
     * identificador.
     *
     * `extras` son referencias al catálogo, que viaja aparte y los resuelve. Éstos
     * no están en ningún catálogo —su dueño es este producto— así que si no
     * viajaran aquí con su nombre y su precio no habría de dónde sacarlos: harían
     * falta tantas consultas como productos tenga la carta.
     */
    extrasPropios: z.array(esquemaExtraPropio),
    /**
     * `T-48` · **Dónde se vende este platillo**, por identificador de sucursal.
     *
     * Enmienda de `ADR-027`, decidida por Isaac el 2026-09-21: «todas deben estar
     * a la par en cuanto a menús; si se hace un cambio sobre el menú general se
     * hará en todas las sucursales». O sea que **la carta es una sola** y lo que
     * una sucursal puede tener no es otra carta: son excepciones, y ésta es la
     * única que existe.
     *
     * ── Lista VACÍA = se vende en todas, y ése es el sentido barato ───────────
     * No es un detalle de codificación. Con el sentido contrario —la lista como
     * «dónde NO se vende»— abrir una sucursal obligaría a repasar cada platillo
     * para no dejarlo colado, y eso es trabajo que aparece justo el día que el
     * dueño está ocupado abriendo un local. Así, una cafetería donde todo se
     * vende en todas partes **no escribe ni una fila**, y una sucursal nueva
     * hereda la carta entera por no decir nada.
     *
     * ── Aquí y no en `esquemaPlatillo`, como `extras` y por lo mismo ──────────
     * El comensal no recibe esta lista: para él el filtro ya ocurrió: la policy
     * `menu_items_publico` de la 053 le sirve lo que se vende donde está, y un
     * platillo que no se vende ahí sencillamente no viaja. Mandarle además la
     * lista sería contarle qué se vende en los otros locales, que no es asunto
     * suyo y es justo lo que el tipo público impide que quepa.
     */
    sucursales: z.array(esquemaId),
});
/**
 * `T-37` · Y el dueño ve una cosa más que el comensal: **el interruptor**.
 *
 * Se define aquí y **no** en `esquemaCategoria` por la misma razón exacta que
 * `archivadoEn`: si una sección apagada cupiera en el contrato público, el día
 * que alguien se despistara en una consulta media carta retirada aparecería en
 * la mesa. Que no quepa en el tipo es la primera de las tres barreras — las
 * otras dos son la policy `menu_categories_publico` y el `where`, y del `where`
 * conviene saber que **aquí no hay ninguno**: `anon` no tiene otorgada
 * `is_visible`, así que el menú público no podría filtrar por ella aunque
 * quisiera. La policy carga sola con el trabajo, y por eso está medida.
 *
 * ── `visible` y no `oculta` ─────────────────────────────────────────────────
 * La insignia de la pantalla dice «OCULTO», que es la negación de esto. Se
 * escribe en positivo igual, para que la polaridad sea la misma en las tres
 * capas —columna `is_visible`, predicado `and is_visible`, campo `visible`—:
 * invertirla en una sola sería un sitio donde equivocarse en cada lectura, y la
 * negación de la vista la hace la vista.
 */
export const esquemaCategoriaDelEditor = esquemaCategoria.extend({
    platillos: z.array(esquemaPlatilloDelEditor),
    /** `false` = apagada. Nunca llega al contrato público, ni con valor ni sin él. */
    visible: z.boolean(),
});
export const esquemaCartaDelEditor = esquemaCarta.extend({
    categorias: z.array(esquemaCategoriaDelEditor),
});
export const esquemaCartasDelEditor = z.object({
    cartas: z.array(esquemaCartaDelEditor),
});
/**
 * La papelera, en su propia lista y no mezclada con la carta.
 *
 * Lleva el nombre de la categoría porque un platillo archivado se lee fuera de
 * su sitio: «Frappé de mango» a secas no le dice a nadie de dónde salió, y
 * restaurarlo es decidir si vuelve ahí.
 */
export const esquemaPlatilloArchivado = z.object({
    id: esquemaId,
    nombre,
    precioCentavos: esquemaCentavos,
    categoriaId: esquemaId,
    categoriaNombre: nombre,
    archivadoEn: z.string().datetime(),
});
export const esquemaPapelera = z.object({
    platillos: z.array(esquemaPlatilloArchivado),
});
/* ── Y lo que el editor MANDA, además de lo de arriba ─────────────────────── */
/**
 * Editar una sección: **el nombre y la descripción**, que es lo que el diálogo
 * «Editar sección» enseña junto. Mover una sección de carta sigue sin ser una
 * operación, y `T-34` sigue sin pedirla.
 *
 * ── Lo que cambió de `T-34` a `T-37`, dicho en voz alta ────────────────────
 * Antes era `{ nombre }` con `nombre` **obligatorio**. Ahora los dos campos son
 * opcionales con «algo tiene que cambiar», que es la forma exacta de
 * `esquemaEditarPlatillo` y la misma razón: quien edita de pie y entre clientes
 * manda **una** cosa, no el objeto entero. Un cuerpo `{ nombre }` de los que ya
 * se mandaban sigue siendo válido, así que ningún productor se rompe — pero es
 * la única forma de este archivo que esta ola relaja en vez de añadir, y va
 * escrito aquí para que no haya que deducirlo del diff.
 *
 * **`descripcion: null` es «bórrala»; ausente es «no la toques».** La diferencia
 * se pierde si se usa `coalesce` en el SQL —en el motor los dos son el mismo
 * nulo—, así que se decide fuera. Idéntico a `esquemaEditarPlatillo`.
 *
 * ── Y el interruptor NO está aquí, a propósito ─────────────────────────────
 * Encender y apagar tiene sus dos rutas (`/ocultar`, `/mostrar`), como archivar
 * y restaurar un platillo. Es su análogo exacto —lo que quita algo de la mesa
 * sin borrarlo— y comparte sus dos propiedades: es idempotente y **deja un
 * renglón firmado en `menu_change_log`**. Escondido como un quinto campo de este
 * `PATCH` sería un cuerpo que hay que leer para saber que medio menú se apagó;
 * con ruta propia es un renglón del registro del servidor que se puede buscar.
 * Mismo argumento que `DELETE /menu/platillos/:id/imagen` en `T-35`.
 */
export const esquemaEditarCategoria = z.object({
    nombre: nombre.optional(),
    descripcion: descripcion.nullable().optional(),
}).strict().refine((c) => Object.keys(c).length > 0, { message: 'Hay que cambiar algo: el nombre o la descripción.' });
/* ── `T-37` · Y los tamaños ───────────────────────────────────────────────── */
/**
 * Alta de un tamaño. El platillo va en la ruta, no en el cuerpo: el tamaño no
 * existe sin él y moverlo de platillo no es una operación que nadie haya pedido
 * —sería borrarlo y crearlo—.
 *
 * `precioCentavos` es **obligatorio**, igual que en `esquemaCrearPlatillo`: un
 * tamaño sin precio no es un tamaño, es una etiqueta. Consecuencia directa, y
 * querida: dar de alta un tamaño exige siempre `menu.price.edit`.
 */
export const esquemaCrearTamano = z.object({
    nombre: nombreDeTamano,
    precioCentavos: esquemaCentavos,
}).strict();
/**
 * Cambio parcial de un tamaño, como el del platillo: todo opcional salvo que
 * **algo** tiene que cambiar.
 *
 * No hay `descripcion: null` que valga aquí —no hay campos nulables—, así que
 * esto es más simple que `esquemaEditarPlatillo` a propósito: un tamaño son dos
 * campos, y quien quiera cambiar los dos manda los dos.
 *
 * Quien manda `precioCentavos` necesita `menu.price.edit`; quien manda sólo el
 * nombre, no. Es la misma regla que ya rige el `PATCH` del platillo, y el
 * servicio la aplica en el mismo sitio.
 */
export const esquemaEditarTamano = z.object({
    nombre: nombreDeTamano.optional(),
    precioCentavos: esquemaCentavos.optional(),
}).strict().refine((c) => Object.keys(c).length > 0, { message: 'Hay que cambiar algo: el nombre o el precio.' });
/* ── `T-35` · La identidad visual de la cafetería ─────────────────────────── */
/**
 * La portada y el logotipo **que ya están puestos**, tal como los lee el panel.
 *
 * Se **añade** a este archivo y no cambia ninguna forma anterior: es una
 * superficie nueva, igual que hizo `subida.ts` en la ola 2. Quien ya construía
 * contra `esquemaCarta` o `esquemaPlatillo` no se entera de que esto existe, que
 * es exactamente lo que el congelado protege.
 *
 * ── Por qué son URLs y no rutas de objeto ───────────────────────────────────
 * Por lo mismo que `esquemaPlatillo.imagenUrl`, y la razón está escrita allí: el
 * navegador **no tiene por qué saber dónde vive Storage**, y si lo supiera habría
 * dos sitios donde equivocarse con el origen. Las compone
 * `storage/url-publica.ts`, el único sitio del proyecto que nombra el cubo.
 *
 * ── Y por qué NO viajan dentro de la carta ──────────────────────────────────
 * Porque no son de ninguna carta. `ADR-013` las llama dos de las tres vías de
 * identidad **de la cafetería**, y una cafetería con dos cartas las tiene una
 * sola vez. Meterlas en `esquemaCartasDelEditor` habría cambiado una forma que
 * otros ya producen —justo lo que la cabecera de este archivo prohíbe— para
 * repetir las mismas dos URLs en cada carta.
 *
 * Nulas es el caso normal durante el piloto: `ADR-013` exige que el menú se vea
 * bien sin ninguna de las dos.
 */
export const esquemaIdentidadDeLaCafeteria = z.object({
    /** Lo primero y lo más grande de la pantalla del comensal — `ADR-013`. */
    portadaUrl: z.string().url().nullable(),
    /** Justo debajo de la portada. La tercera vía de identidad es la paleta. */
    logoUrl: z.string().url().nullable(),
}).strict();
/* ── `T-38` · Los grupos de tamaños ───────────────────────────────────────── */
/**
 * Un grupo de tamaños: **una plantilla, no un vínculo.**
 *
 * Una cafetería define «Café caliente (C · M · G)» una vez, y al editar un
 * platillo elige uno y **se le crean esas fichas**. Ahí acaba el papel del
 * grupo: después, cada platillo es dueño de sus fichas, y editar el grupo más
 * tarde **no toca ningún platillo ya creado**.
 *
 * Lo decidió Isaac, y no es una preferencia de forma: con un vínculo vivo,
 * renombrar «Grande» a «Familiar» en un grupo reescribiría media carta de veinte
 * platillos con una edición de una línea, y encima habría que decidir qué pasa
 * con los precios —que sí son de cada platillo—. El razonamiento completo, con
 * lo que se descartó, vive en la cabecera de la migración 037.
 *
 * Lo que esta forma NO tiene, y es la mitad que importa: **ningún precio**. Un
 * «Grande» de café no cuesta lo que un «Grande» de licuado, así que un precio
 * sugerido acertaría en casi ningún platillo y se publicaría igual. Las fichas
 * que nacen al aplicar el grupo toman el precio que el platillo ya tenía, así
 * que **aplicar una plantilla no cambia ni un precio de la carta**.
 */
export const esquemaGrupoDeTamanos = z.object({
    id: esquemaId,
    nombre: nombreDeGrupo,
    /** El orden del desplegable. Lo decide la dueña, igual que el de los apartados. */
    orden: z.number().int(),
    /** Las etiquetas, **en su orden**. Dos o más — ver `esquemaEtiquetasDeGrupo`. */
    etiquetas: z.array(nombreDeTamano),
});
export const esquemaGruposDeTamanos = z.object({
    grupos: z.array(esquemaGrupoDeTamanos),
});
/**
 * Las etiquetas de un grupo, tal como se mandan.
 *
 * **`.min(2)` y no `.min(1)`, por `ADR-018`:** «Único» no es un tamaño que se
 * elija — un platillo o no tiene fichas, o tiene dos o más. Un grupo de una sola
 * etiqueta produciría, al aplicarse, exactamente el estado que el ADR declara
 * inexistente. El motor lo respalda con un disparador de restricción diferido
 * (037); este `.min(2)` es lo que hace que por HTTP salga un 400 legible en vez
 * de un fallo en el COMMIT.
 *
 * **`.max(8)`** porque la tabla de tamaños del armador tiene el ancho que tiene:
 * una plantilla de doce etiquetas no se puede aplicar a nada que quepa en una
 * pantalla, y el sitio donde eso debe doler es al definirla.
 *
 * **Y sin repetidas.** Dos etiquetas iguales en un grupo producirían dos fichas
 * con el mismo nombre en el platillo, que es lo que la 034 prohíbe con su
 * `unique (tenant_id, menu_item_id, name)` porque «se leen una al lado de la
 * otra, con dos precios distintos». Vale más pararlo aquí: una plantilla rota se
 * aplica muchas veces. La comparación es sobre el texto ya recortado, que es lo
 * que se guardaría.
 */
export const esquemaEtiquetasDeGrupo = z.array(nombreDeTamano).min(2).max(8).refine((lista) => new Set(lista).size === lista.length, { message: 'Un grupo no puede repetir una etiqueta: quedarían dos tamaños con el mismo nombre.' });
export const esquemaCrearGrupoDeTamanos = z.object({
    nombre: nombreDeGrupo,
    etiquetas: esquemaEtiquetasDeGrupo,
}).strict();
/**
 * Cambio parcial de un grupo: el nombre, las etiquetas, o los dos.
 *
 * Las etiquetas llegan **enteras y en su orden**, no «añade una»: es la misma
 * decisión que `esquemaReordenar` —«con la lista completa, quien manda una lista
 * que no coincide con lo que hay se entera»— y aquí cuesta todavía menos, porque
 * una plantilla son tres palabras.
 *
 * > [!danger] Esto **no toca ningún platillo**, y es la decisión entera
 * > Cambiar aquí «Grande» por «Familiar» cambia la plantilla y nada más. Los
 * > platillos que ya la usaron conservan sus fichas y sus precios. No hace falta
 * > prohibirlo en ningún sitio: no existe ninguna columna que una una ficha con
 * > su grupo, así que no hay por dónde propagar.
 */
export const esquemaEditarGrupoDeTamanos = z.object({
    nombre: nombreDeGrupo.optional(),
    etiquetas: esquemaEtiquetasDeGrupo.optional(),
}).strict().refine((c) => Object.keys(c).length > 0, { message: 'Hay que cambiar algo: el nombre o las etiquetas.' });
/**
 * Aplicar un grupo a un platillo. El platillo va en la ruta; el grupo, aquí.
 *
 * Se rechaza si el platillo **ya tiene fichas**, y no se puede pedir lo
 * contrario: no hay un `reemplazar: true` en este cuerpo a propósito. Elegir una
 * opción de un desplegable no es pedir que se tiren tres precios, y una bandera
 * que el panel acabaría poniendo en `true` fijo —porque es la única forma de que
 * el botón funcione— no guarda nada. Quien quiera cambiar de plantilla quita sus
 * fichas una por una, y cada baja queda firmada en `menu_change_log` con el
 * precio que tenía.
 */
export const esquemaAplicarGrupoDeTamanos = z.object({
    grupoId: esquemaId,
}).strict();
/**
 * La comparación es **por etiquetas y en orden**, y las dos mitades son
 * deliberadas:
 *
 *  · Por etiquetas y no por precios, porque los precios son de cada platillo
 *    desde el primer día y no forman parte de ninguna plantilla.
 *  · **En orden**, porque el orden es parte de la plantilla: «Café caliente
 *    (C · M · G)» lo dice en el nombre. Quien arrastra «Grande» arriba ha
 *    personalizado su platillo, y decirlo es exacto, no severo.
 *
 * Si dos grupos tuvieran exactamente las mismas etiquetas en el mismo orden, se
 * devuelve **el primero de la lista** —que llega ordenada por `orden`— y no
 * «personalizado»: las dos respuestas serían igual de ciertas, y «personalizado»
 * sería la única que además es falsa.
 */
export function plantillaDelPlatillo(tamanos, grupos) {
    if (tamanos.length === 0)
        return { tipo: 'sin-tamanos' };
    const etiquetas = tamanos.map((t) => t.nombre);
    const grupo = grupos.find((g) => g.etiquetas.length === etiquetas.length
        && g.etiquetas.every((e, i) => e === etiquetas[i]));
    return grupo === undefined ? { tipo: 'personalizado' } : { tipo: 'grupo', grupo };
}
/**
 * Y el de un GRUPO del catálogo, con el mismo tope de 40 que el de un extra.
 *
 * Mismo tope y no uno mayor aunque esto se lea como encabezado y no como chip:
 * «Elige tu leche» son 14 caracteres y «Jarabes y endulzantes» son 21. Un
 * encabezado de sesenta caracteres sobre una nube de chips de veinte deja de
 * leerse como un rótulo.
 */
const nombreDeGrupoDeExtras = z.string().trim().min(1).max(40);
/**
 * `T-38` · Un extra del catálogo **de la cafetería**.
 *
 * El precio vive aquí y en ningún otro sitio: subir el shot extra se hace una vez
 * y vale para todos los platillos que lo tengan habilitado. Que un platillo lo
 * acepte se dice con `PlatilloDelEditor.extras`, que es una lista de
 * identificadores y **no** una copia de estas fichas — copiarlas sería repetir un
 * precio por platillo, que es la segunda verdad que este diseño existe para no
 * tener.
 *
 * ── Y por qué NO hay un `esquemaExtraPublico` ───────────────────────────────
 * Porque esta ola es «panel primero, mesa después». Nada de esto llega al
 * comensal: la migración 038 no otorga una sola columna a `anon` y
 * `menu-publico.ts` no se toca. Cuando llegue, la forma pública será **otra** —
 * sin `disponible`, que es dato del dueño— y se añadirá extendiendo, como hizo
 * `esquemaPlatilloDelEditor`.
 */
export const esquemaExtra = esquemaExtraPropio.extend({
    /**
     * A qué **grupo del catálogo** pertenece, o `null` si es **propio de un
     * producto** — `T-46`.
     *
     * > [!warning] Nulo cambió de significado en la migración 040
     * > Hasta entonces era «suelto»: no forma parte de ninguna regla de selección.
     * > Isaac lo retiró — «no habrán extras sin grupo»— y en su sitio hay una regla
     * > que un CHECK dice entera: **o pertenece a un grupo, o es de un producto.
     * > Nunca las dos, nunca ninguna.**
     * >
     * > Así que un `grupoId` nulo ya no significa «no tiene dueño»: significa que
     * > su dueño es el producto dentro del cual viaja. `GET /menu/extras` no
     * > devuelve ninguno — el catálogo son los de la casa.
     *
     * ── Uno como mucho, y por qué no varios ───────────────────────────────────
     * Si «Leche de almendras» estuviera a la vez en «Elige una leche» (máx 1) y en
     * «Los que quieras» (sin tope), habría que decidir cuál manda y explicárselo a
     * quien prepara la carta. `ADR-020` ya dijo que una regla que hay que explicar
     * es una regla que sobra. La cabecera de la migración 039 lo razona entero.
     */
    grupoId: esquemaId.nullable(),
});
/**
 * Crear un extra **sólo para este producto** — `POST /menu/platillos/:id/extras-propios`.
 *
 * Nombre y precio, y nada más. Sin grupo, porque su dueño es el producto; y sin
 * `disponible`, porque nace disponible — nadie da de alta un extra para
 * anunciarlo agotado.
 *
 * Es para lo que sólo tiene sentido en un sitio: «con galleta encima» de un
 * frappé concreto. Meterlo en el catálogo obligaría a inventarle un grupo —lo
 * que la regla nueva quiere evitar— y a que saliera en la pantalla de Extras,
 * estorbando a los veinte que sí son de la casa.
 */
export const esquemaCrearExtraPropio = z.object({
    nombre: nombreDeExtra,
    precioCentavos: esquemaCentavos,
}).strict();
/**
 * `T-38` · Un grupo del catálogo: «Elige tu leche», «Toppings».
 *
 * > [!warning] `minimo` y `maximo` HOY SÓLO INFORMAN
 * > FialRest no toma pedidos: el comensal lee. Nada los comprueba —ni la base, ni
 * > la API, ni el editor— y `ADR-020` §3 lo dejó escrito para que nadie los dé
 * > por aplicados. Se modelan ahora porque dos enteros son baratos hoy y caros de
 * > migrar con datos dentro.
 *
 * **Se llama `GrupoDeExtras` y nunca `Grupo` a secas**, que es una consecuencia
 * literal de `ADR-020`: en este esquema hay dos cosas que se llaman grupo —los de
 * tamaños y los de extras— y confundirlas es una bomba de relojería.
 */
export const esquemaGrupoDeExtras = z.object({
    id: esquemaId,
    nombre: nombreDeGrupoDeExtras,
    /** Cuántos hay que elegir como mínimo. 0 = opcional, que es lo normal. */
    minimo: z.number().int().min(0).max(10),
    /**
     * Cuántos como máximo, o `null` = **sin tope**.
     *
     * Nulo y no un 10 que significara «todos»: el 10 mentiría el día que el
     * catálogo tenga once, y «los toppings que quieras» es un caso de la tabla de
     * `ADR-020` §3, no un límite alto.
     */
    maximo: z.number().int().min(1).max(10).nullable(),
    /** El orden en que se pintan. Lo decide la dueña, como las secciones. */
    orden: z.number().int(),
});
/**
 * El catálogo entero: las fichas **y sus grupos**, en la misma respuesta.
 *
 * **Envuelto y no un arreglo suelto**, como `cartas` y `platillos`: un cuerpo que
 * hoy es un arreglo no puede crecer mañana sin romper a todo el que lo consuma —
 * y esta ola es justamente la prueba, porque le acaba de crecer un campo.
 *
 * ── Por qué los grupos viajan AQUÍ y no en una ruta aparte ────────────────
 * Porque no se usan por separado ni una sola vez: cada sitio que pinta la nube de
 * chips necesita las dos cosas a la vez —el nombre del grupo como encabezado y
 * sus fichas debajo—, así que dos rutas serían dos viajes encadenados para pintar
 * una pantalla, sobre el wifi de una cafetería. Es al revés que el catálogo
 * frente a la carta, que sí se separaron: allí lo que se evitaba era **repetir el
 * precio de un extra en cada platillo**, y aquí no hay nada que repetir.
 *
 * El campo se llama `gruposDeExtras` y no `grupos`: ver `esquemaGrupoDeExtras`.
 */
export const esquemaCatalogoDeExtras = z.object({
    extras: z.array(esquemaExtra),
    gruposDeExtras: z.array(esquemaGrupoDeExtras),
    /**
     * `T-41` · **En cuántos productos se usa cada extra.** Un renglón por extra que
     * al menos uno acepta; los que no usa nadie no viajan.
     *
     * ── Por qué viene del servidor y no se cuenta en el navegador ─────────────
     * El panel ya tiene la carta y podría contarlo solo — y **mentiría**. La carta
     * no trae los productos archivados, y el `409` que rechaza borrar un extra los
     * cuenta todos, porque quien rechaza es la llave foránea. La pantalla diría «en
     * 0 productos» junto a un rechazo que dice «en 3», y quien lo lee no tiene forma
     * de resolver la contradicción.
     *
     * ── Por qué DOS números ──────────────────────────────────────────────────
     * `productos` es el que manda para borrar: es el que ve el motor. `enLaCarta`
     * es el que le importa a la dueña, porque es lo que el comensal puede pedir.
     * Cuando coinciden —el caso normal— la pantalla enseña uno solo; cuando no,
     * dice dónde está la diferencia en vez de dejar un número inexplicable.
     */
    usos: z.array(z.object({
        extraId: esquemaId,
        /** Todos los productos que lo aceptan, **incluida la papelera**. Nunca cero. */
        productos: z.number().int().min(1),
        /** Sólo los que están en la carta. Puede ser cero si todos están archivados. */
        enLaCarta: z.number().int().min(0),
    })),
});
/**
 * Dar de alta un grupo.
 *
 * `minimo` y `maximo` llegan con omisión —0 y sin tope— porque es el caso normal
 * y porque el panel no pide dos números: pide dos decisiones con palabras
 * («¿es obligatorio?», «¿cuántos puede elegir?») y las traduce. Ver `ADR-020` §3:
 * «los números son el modelo, no la interfaz».
 *
 * `.strict()` como sus vecinos: un campo de más es un cliente desactualizado o un
 * dedo, y las dos cosas se contestan mejor con un 400 que aceptándolas en
 * silencio.
 */
export const esquemaCrearGrupoDeExtras = z.object({
    nombre: nombreDeGrupoDeExtras,
    minimo: z.number().int().min(0).max(10).default(0),
    maximo: z.number().int().min(1).max(10).nullable().default(null),
}).strict().refine((g) => g.maximo === null || g.minimo <= g.maximo, { message: 'El mínimo no puede pasar del máximo: sería un grupo del que no se puede elegir nada.' });
/**
 * Editarlo, **por partes**, como todo `PATCH` de este módulo.
 *
 * `maximo: null` es «quítale el tope» y ausente es «no lo toques» — la misma
 * distinción que `descripcion` en un platillo, y por eso no se puede resolver con
 * un `coalesce`: en el motor los dos son el mismo nulo.
 *
 * La coherencia entre los dos **no se comprueba aquí** y sí en el motor, y es a
 * propósito: un `PATCH` parcial no conoce el valor que no trae. Cambiar sólo el
 * mínimo a 3 en un grupo con máximo 1 lo para el CHECK
 * `menu_extra_groups_min_no_pasa_al_max`, que sí ve la fila entera.
 */
export const esquemaEditarGrupoDeExtras = z.object({
    nombre: nombreDeGrupoDeExtras.optional(),
    minimo: z.number().int().min(0).max(10).optional(),
    maximo: z.number().int().min(1).max(10).nullable().optional(),
}).strict().refine((g) => Object.keys(g).length > 0, { message: 'Hay que cambiar algo: el nombre, el mínimo o el máximo.' });
/**
 * A qué grupo va un extra. `grupoId: null` lo saca de todos.
 *
 * Ruta propia y no un campo del `PATCH` del extra, por lo mismo que `T-35` sacó
 * «quitar la foto» a su propia ruta: son dos cosas distintas —cómo se llama y
 * cuánto cuesta, frente a de qué familia es— y mezclarlas haría que renombrar un
 * extra pudiera moverlo de grupo por un campo que el panel mandó de más.
 */
export const esquemaMoverExtraDeGrupo = z.object({
    /**
     * `T-46` · **Ya no admite nulo.** Sacar un extra de su grupo lo dejaría suelto,
     * y los sueltos dejaron de existir con la migración 040 — el CHECK
     * `menu_extras_tiene_dueno` lo rechazaría con un error que esta capa no sabe
     * traducir. Cambiarlo de grupo sí; dejarlo sin ninguno, no. Lo que sustituye
     * a «sacarlo» es borrarlo, que ya tiene su ruta y su confirmación.
     */
    grupoId: esquemaId,
}).strict();
/**
 * Alta de un extra en el catálogo.
 *
 * `precioCentavos` es **obligatorio**, igual que en `esquemaCrearPlatillo` y
 * `esquemaCrearTamano`: un extra sin precio no es un extra, es una etiqueta — y
 * cero ya significa «sin cargo», así que no falta ninguna forma de expresarlo.
 * Consecuencia directa y querida: dar de alta un extra exige `menu.price.edit`.
 *
 * `disponible` **no está aquí**: un extra nace disponible, que es el caso normal,
 * y apagarlo tiene sus dos rutas. Meterlo en el alta invitaría a crear catálogo
 * apagado, que es un estado que nadie ha pedido.
 */
export const esquemaCrearExtra = z.object({
    nombre: nombreDeExtra,
    precioCentavos: esquemaCentavos,
    /**
     * `T-46` · **Obligatorio.** Un extra del catálogo pertenece a un grupo, y no
     * hay tercera opción: lo de fuera del catálogo se crea desde su producto, con
     * `esquemaCrearExtraPropio`.
     *
     * Se pide aquí y no se deja para un `PATCH` posterior porque el CHECK de la
     * base no admite el estado intermedio: un extra sin grupo y sin producto no
     * puede existir ni un instante, ni siquiera dentro de una transacción.
     */
    grupoId: esquemaId,
}).strict();
/**
 * Cambio parcial de un extra, como el del platillo y el del tamaño: todo
 * opcional salvo que **algo** tiene que cambiar.
 *
 * Quien manda `precioCentavos` necesita `menu.price.edit`; quien manda sólo el
 * nombre, no. Es la misma regla que ya rige los dos `PATCH` anteriores, y el
 * servicio la aplica en la misma función.
 *
 * ── Y el interruptor NO está aquí, a propósito ─────────────────────────────
 * Apagar y encender tienen sus dos rutas (`/apagar`, `/encender`), exactamente
 * como el interruptor de una sección y por el mismo argumento escrito en
 * `esquemaEditarCategoria`: **apagar «Crema batida» la quita de veinte productos
 * a la vez**, y deja un renglón firmado en `menu_change_log` por el simple hecho
 * de ocurrir. Escondido como un tercer campo de este `PATCH` sería un cuerpo que
 * hay que leer para saber que un cargo desapareció de media carta; con ruta
 * propia es un renglón del registro del servidor que se puede buscar.
 */
export const esquemaEditarExtra = z.object({
    nombre: nombreDeExtra.optional(),
    precioCentavos: esquemaCentavos.optional(),
}).strict().refine((c) => Object.keys(c).length > 0, { message: 'Hay que cambiar algo: el nombre o el precio.' });
/**
 * Lo que devuelve el borrado rechazado: **cuántos platillos lo usan**.
 *
 * La migración 038 decidió que un extra en uso no se borra —la llave foránea es
 * `restrict`— y el 409 que sale de ahí sería inútil sin este número: «no se puede
 * borrar» deja a quien lo lee sin saber si le faltan dos casillas o veinte, y sin
 * saber si merece la pena el desenganche masivo.
 *
 * Va en el cuerpo del 409 y no en un `GET` aparte a propósito: preguntarlo antes
 * sería una comprobación que corre siempre para un caso raro, y además dejaría
 * una carrera entre la pregunta y el borrado — el mismo argumento con el que
 * `borrarPlatillo` no comprueba el archivado antes de intentarlo.
 */
export const esquemaExtraEnUso = z.object({
    statusCode: z.literal(409),
    error: z.string(),
    mensaje: z.string(),
    /** Cuántos platillos lo tienen habilitado ahora mismo. Nunca cero. */
    platillos: z.number().int().min(1),
});
/* ── `T-38` · Los extras de una SECCIÓN, que es una plantilla ─────────────── */
/**
 * Lo que hay que saber **antes** de pulsar «Aplicar»: qué extras tienen ya los
 * productos de esta sección, y cuántos de ellos tiene cada uno.
 *
 * ── Por qué una cuenta y no un booleano ───────────────────────────────────
 * Porque una sección **no tiene extras**: los tienen sus productos, cada uno los
 * suyos, y eso es la decisión de `ADR-020` —la sección es una plantilla, no un
 * dueño—. Un booleano «la sección lo tiene» tendría que inventar una regla para
 * el caso normal, que es el intermedio: cuatro de seis lo aceptan porque alguien
 * lo aplicó y luego se lo quitó a dos. Un `sí` mentiría y un `no` también.
 *
 * Con la cuenta, la pantalla puede decir «en 4 de 6» y quien lee entiende de una
 * vez por qué aplicar no es idempotente con lo que ya hay y qué va a cambiar.
 *
 * ── Y por qué `productos` viaja aquí ──────────────────────────────────────
 * Es el denominador. Sin él, «en 4» no dice nada, y pedir la carta entera sólo
 * para contar los de una sección sería un viaje de más sobre el wifi de una
 * cafetería. **Cuenta los de la carta, no los archivados**: aplicar no toca la
 * papelera, y un denominador que incluyera lo archivado haría que «6 de 8» fuera
 * imposible de completar.
 */
export const esquemaExtrasDeSeccion = z.object({
    /** Cuántos productos hay en la sección, sin contar la papelera. Puede ser 0. */
    productos: z.number().int().min(0),
    /**
     * Un renglón por extra del catálogo que **al menos un** producto de la sección
     * acepta. Los que no tiene ninguno no viajan: su cuenta es cero y la pantalla
     * ya conoce el catálogo entero.
     */
    extras: z.array(z.object({
        extraId: esquemaId,
        /** Cuántos productos de la sección lo aceptan. Nunca cero — ver arriba. */
        productos: z.number().int().min(1),
    })),
});
/**
 * El cuerpo de «Aplicar a los N productos».
 *
 * `min(1)` y no la lista vacía: aplicar nada es una petición que no hace nada y
 * contesta 200, que es la peor respuesta posible — dice que se hizo algo. Es la
 * misma razón por la que `aplicarGrupoDeTamanos` rechaza un grupo inventado en
 * vez de insertar cero fichas.
 *
 * El tope son cuarenta, que es el catálogo entero y pico: no es una regla de
 * negocio, es el mismo cinturón que llevan las demás listas del contrato para
 * que un cuerpo de un megabyte no llegue a la base.
 */
export const esquemaAplicarExtrasASeccion = z.object({
    extras: z.array(esquemaId).min(1).max(40),
});
/**
 * Lo que contesta: **qué cambió de verdad**, no qué se pidió.
 *
 * `casillasNuevas` cuenta las filas que esta petición creó, y por eso puede ser
 * menor que `productos × extras` e incluso **cero**: lo que un producto ya tenía
 * no se vuelve a escribir. Cero es un resultado legítimo —«ya lo tenían todos»—
 * y la pantalla lo dice con esas palabras en vez de fingir un cambio.
 *
 * Sin este número, aplicar a seis productos contestaría lo mismo tanto si escribió
 * dieciocho casillas como si no escribió ninguna, y quien lo pulsó no sabría si
 * el botón hizo algo. Es el mismo criterio con el que el 409 de borrar un extra
 * lleva dentro cuántos platillos lo usan.
 */
export const esquemaExtrasAplicados = z.object({
    /** A cuántos productos se aplicó. El denominador de la frase de confirmación. */
    productos: z.number().int().min(1),
    /** Cuántas casillas nuevas se escribieron. Cero si ya los tenían todos. */
    casillasNuevas: z.number().int().min(0),
});
/* ── `T-39` · Mover: a qué sección y a qué posición ───────────────────────── */
/**
 * `POST /menu/platillos/:id/mover` — `ADR-022`.
 *
 * Las **dos mitades en un solo cuerpo**, y en una sola transacción: cambiar de
 * sección y colocar en su sitio. Media operación aplicada sería un producto en la
 * sección nueva y en el lugar equivocado, que es peor que no haber movido nada.
 *
 * ── `despuesDe` y no un número de posición ────────────────────────────────
 * Un número obliga a contar filas y a saber si se cuenta desde uno o desde cero.
 * Un identificador no se cuenta: es «detrás de este». La pantalla lo enseña por
 * su nombre —«después de Latte»—, que es el mismo criterio con el que `ADR-020`
 * decidió que las reglas de un grupo se eligen con palabras.
 *
 * **`null` es «al principio»**, y es un estado con significado y no un hueco: no
 * hay ningún producto detrás del cual ponerlo, porque va delante de todos. «Al
 * final» no necesita valor propio — es `despuesDe` el último.
 *
 * El destino se comprueba **en el servidor**: `despuesDe` tiene que ser de
 * `categoriaId`. Si no, el orden que saldría no es el que pidió nadie.
 */
export const esquemaMoverPlatillo = z.object({
    /** A qué sección va. La misma en la que está, si sólo se reordena. */
    categoriaId: esquemaId,
    /** Detrás de cuál queda, o `null` para dejarlo el primero. */
    despuesDe: esquemaId.nullable(),
}).strict();
/**
 * `POST /menu/categorias/:id/mover` — lo mismo para una sección.
 *
 * Sin `cartaId`: una sección no cambia de carta. Podría, y **no se ofrece a
 * propósito** — una cafetería tiene una carta en el piloto, y la segunda existe
 * para una sucursal con otra oferta, no para mover apartados entre ellas. El día
 * que haga falta es un campo más, no otro diseño.
 */
export const esquemaMoverCategoria = z.object({
    /** Detrás de cuál queda, o `null` para dejarla la primera. */
    despuesDe: esquemaId.nullable(),
}).strict();
//# sourceMappingURL=menu.js.map